import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Square, Download, RotateCw, Zap, Cpu, CheckCircle2, Infinity as InfinityIcon } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useBrowserTTS } from "../hooks/useBrowserTTS";
import { useKokoroTTS } from "../hooks/useKokoroTTS";
import { KOKORO_VOICES } from "../lib/kokoroVoices";
import { useAuth } from "../context/AuthContext";
import { queueIntroOfferReminder } from "../lib/introOffer";
import { FREE_LIMITS } from "../lib/plans";

function FilledSlider({ value, min, max, step, onChange, color, testid }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="relative h-2 w-full bg-zinc-800">
      <div className="absolute inset-y-0 left-0" style={{ width: `${pct}%`, background: color }} />
      <div
        className="absolute top-1/2 -translate-y-1/2 h-5 w-1 bg-white"
        style={{ left: `calc(${pct}% - 2px)` }}
      />
      <input
        type="range"
        aria-label={testid === "pitch-slider" ? "Pitch" : "Speech speed"}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={testid}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
    </div>
  );
}

export default function Studio() {
  const [engine, setEngine] = useState("browser"); // "browser" (low) | "kokoro" (high)
  const [text, setText] = useState(
    "Welcome to the local voice studio. Type anything here and turn it into speech, generated entirely on your machine."
  );

  const browser = useBrowserTTS();
  const kokoro = useKokoroTTS();
  const { user, usage, isPaid, openAuth, openPricing, consume, refundUsage, openBillingPortal, logout } = useAuth();

  const [browserVoiceURI, setBrowserVoiceURI] = useState("");
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);

  const [kokoroVoice, setKokoroVoice] = useState("af_heart");
  const [speed, setSpeed] = useState(1);

  const [audioUrl, setAudioUrl] = useState(null);
  const [playingKokoro, setPlayingKokoro] = useState(false);
  const audioRef = useRef(null);

  // default browser voice
  useEffect(() => {
    if (!browserVoiceURI && browser.voices.length) {
      const en = browser.voices.find((v) => v.lang && v.lang.startsWith("en"));
      setBrowserVoiceURI((en || browser.voices[0]).voiceURI);
    }
  }, [browser.voices, browserVoiceURI]);

  // invalidate any cached audio when the inputs that shape it change
  useEffect(() => {
    setAudioUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, [text, kokoroVoice, speed]);

  // surface worker errors
  useEffect(() => {
    if (kokoro.error) toast.error(kokoro.error);
  }, [kokoro.error]);

  const isHigh = engine === "kokoro";
  const isPlaying = isHigh ? playingKokoro : browser.speaking;

  const englishVoices = useMemo(
    () => browser.voices.filter((v) => v.lang && v.lang.startsWith("en")),
    [browser.voices]
  );
  const otherVoices = useMemo(
    () => browser.voices.filter((v) => !(v.lang && v.lang.startsWith("en"))),
    [browser.voices]
  );

  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;

  const quotaLimit = isHigh ? FREE_LIMITS.high : FREE_LIMITS.low;
  const quotaUsed = usage ? (isHigh ? usage.high.used : usage.low.used) : 0;
  const quotaLeft = Math.max(0, quotaLimit - quotaUsed);
  const quotaPct = Math.min(100, (quotaUsed / quotaLimit) * 100);
  const willExceed = !!user && !isPaid && text.length > quotaLeft;

  const generate = async () => {
    const charge = await guard("high", text.length);
    if (!charge) return null;
    try {
      const blob = await kokoro.generate(text, kokoroVoice, speed);
      const url = URL.createObjectURL(blob);
      setAudioUrl((previousUrl) => {
        if (previousUrl) URL.revokeObjectURL(previousUrl);
        return url;
      });
      queueIntroOfferReminder();
      return url;
    } catch (error) {
      if (charge.charge_id) {
        try {
          await refundUsage(charge.charge_id);
        } catch {
          toast.error("Generation failed and quota restoration is still pending.");
        }
      }
      throw error;
    }
  };

  const guard = async (engine, chars) => {
    if (!user) {
      openAuth();
      return false;
    }
    try {
      const res = await consume(engine, chars);
      if (!res.allowed) {
        openPricing(
          `You've used all ${res.limit.toLocaleString()} free ${
            engine === "high" ? "high-quality" : "low-quality"
          } characters. Go unlimited to keep generating.`
        );
        return null;
      }
      return res;
    } catch {
      toast.error("Couldn't verify your usage — please try again.");
      return null;
    }
  };

  const startKokoroLoad = () => {
    toast.info("Loading the high-quality model — first run downloads ~86MB.");
    kokoro.load();
  };

  const handlePlay = async () => {
    if (!text.trim()) {
      toast.error("Nothing to speak — type some text first.");
      return;
    }
    if (!user) {
      openAuth();
      return;
    }
    if (!isHigh) {
      if (browser.paused) return browser.resume();
      if (!browser.supported || browser.voices.length === 0) {
        toast.error("No system voices available in this browser — try High Quality instead.");
        return;
      }
      const charge = await guard("low", text.length);
      if (!charge) return;
      browser.speak(text, { voiceURI: browserVoiceURI, rate, pitch });
      queueIntroOfferReminder();
      return;
    }
    if (kokoro.status !== "ready") {
      if (kokoro.status === "idle" || kokoro.status === "error") startKokoroLoad();
      return;
    }
    try {
      let url = audioUrl;
      if (!url) url = await generate();
      if (!url) return;
      setTimeout(() => {
        if (audioRef.current) audioRef.current.play();
      }, 30);
    } catch {
      /* error surfaced via effect */
    }
  };

  const handlePause = () => {
    if (!isHigh) return browser.pause();
    if (audioRef.current) audioRef.current.pause();
  };

  const handleStop = () => {
    if (!isHigh) return browser.stop();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlayingKokoro(false);
    }
  };

  const handleGenerate = async () => {
    if (!isHigh) {
      toast.info("Generation & WAV export require the High Quality engine.");
      return;
    }
    if (!text.trim()) {
      toast.error("Nothing to generate — type some text first.");
      return;
    }
    if (!user) {
      openAuth();
      return;
    }
    if (kokoro.status !== "ready") {
      if (kokoro.status === "idle" || kokoro.status === "error") startKokoroLoad();
      return;
    }
    try {
      toast.info("Generating audio…");
      const url = await generate();
      if (url) toast.success("Audio generated — press Play or download the WAV.");
    } catch {
      /* handled */
    }
  };

  const handleDownload = async () => {
    if (!text.trim()) {
      toast.error("Nothing to download — type some text first.");
      return;
    }
    if (!isHigh) {
      toast.info("WAV export requires the High Quality engine — switch engines to save a file.");
      return;
    }
    if (!user) {
      openAuth();
      return;
    }
    if (kokoro.status !== "ready") {
      if (kokoro.status === "idle" || kokoro.status === "error") startKokoroLoad();
      else toast.info("Model still loading…");
      return;
    }
    try {
      let url = audioUrl;
      if (!url) {
        toast.info("Generating audio…");
        url = await generate();
      }
      if (!url) return;
      const a = document.createElement("a");
      a.href = url;
      a.download = `speech-${kokoroVoice}-${Date.now()}.wav`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("WAV downloaded.");
    } catch {
      /* handled */
    }
  };

  const switchEngine = (next) => {
    if (next === engine) return;
    handleStop();
    setEngine(next);
  };

  const modelStatusText = () => {
    if (kokoro.status === "ready") return `READY · ${(kokoro.device || "wasm").toUpperCase()}`;
    if (kokoro.status === "loading") return `LOADING ${kokoro.progress}%`;
    if (kokoro.status === "error") return "LOAD FAILED · RETRY";
    return "NOT LOADED";
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white font-mono flex flex-col">
      {/* slim brand strip */}
      <div className="flex items-center justify-between px-4 md:px-8 py-4 border-b border-zinc-900">
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand-home-link">
          <div className="h-7 w-7 bg-volt flex items-center justify-center">
            <Zap className="text-black" size={16} strokeWidth={3} />
          </div>
          <span className="hidden min-[400px]:inline font-display text-xl tracking-tight group-hover:text-volt transition-colors duration-100">
            LOCAL VOICE
          </span>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          {user && (
            <>
              {isPaid ? (
                <span
                  data-testid="usage-pill"
                  className="hidden sm:inline px-2.5 py-1 bg-volt text-black text-[11px] font-bold uppercase tracking-widest"
                >
                  Unlimited
                </span>
              ) : usage ? (
                <span
                  data-testid="usage-pill"
                  className="hidden sm:inline px-2.5 py-1 border border-zinc-800 text-[11px] tracking-wider text-zinc-400"
                >
                  {isHigh ? "HQ" : "LQ"}{" "}
                  {Math.max(
                    0,
                    (isHigh ? usage.high : usage.low).limit - (isHigh ? usage.high : usage.low).used
                  ).toLocaleString()}{" "}
                  left
                </span>
              ) : null}
            </>
          )}
          {user && isPaid && (
            <button
              onClick={async () => {
                try {
                  await openBillingPortal();
                } catch (error) {
                  toast.error(error?.response?.data?.detail || "Could not open billing management");
                }
              }}
              data-testid="manage-billing-btn"
              className="hidden sm:inline px-3 py-1.5 border border-zinc-800 text-zinc-300 text-[11px] font-bold uppercase tracking-widest hover:bg-zinc-800 transition-colors duration-100"
            >
              Billing
            </button>
          )}
          {!isPaid && (
            <button
              onClick={() => openPricing()}
              data-testid="upgrade-btn"
              className="px-3 py-1.5 bg-volt text-black text-[11px] font-bold uppercase tracking-widest hover:bg-volt-hover transition-colors duration-100"
            >
              Upgrade
            </button>
          )}
          {user ? (
            <div className="flex items-center gap-2">
              <span
                data-testid="user-email"
                className="hidden md:inline text-[11px] text-zinc-500 max-w-[140px] truncate"
              >
                {user.name || user.email}
              </span>
              <button
                onClick={logout}
                data-testid="logout-btn"
                className="px-3 py-1.5 border border-zinc-800 text-zinc-300 text-[11px] font-bold uppercase tracking-widest hover:bg-zinc-800 transition-colors duration-100"
              >
                Logout
              </button>
            </div>
          ) : (
            <button
              onClick={openAuth}
              data-testid="signin-btn"
              className="px-3 py-1.5 border border-zinc-700 text-white text-[11px] font-bold uppercase tracking-widest hover:bg-zinc-800 transition-colors duration-100"
            >
              Sign In
            </button>
          )}
        </div>
      </div>

      <div className="flex-grow flex flex-col p-3 md:p-6 lg:p-8">
        <div className="w-full max-w-[1400px] mx-auto flex flex-col border border-zinc-800 bg-zinc-800 gap-[1px] flex-grow">
          {/* TOP: input + sidebar */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-[1px] flex-grow">
            {/* INPUT PANEL */}
            <section className={`bg-[#0d0d0d] flex flex-col min-h-[46vh] ${isPlaying ? "shadow-[inset_0_0_55px_rgba(212,255,0,0.04)]" : ""}`}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-900">
                <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-500">
                  Input · UTF-8
                </span>
                <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-500" data-testid="char-count">
                  {wordCount} words · {text.length} chars
                </span>
              </div>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type something to speak…"
                spellCheck={true}
                aria-label="Text to convert to speech"
                data-testid="tts-textarea"
                className="flex-grow w-full resize-none bg-transparent text-white placeholder-zinc-700 text-xl md:text-2xl leading-relaxed px-6 py-6 focus:outline-none focus:ring-0"
              />
              <div className="px-6 py-4 border-t border-zinc-900 min-h-[64px] flex items-center">
                <audio
                  ref={audioRef}
                  src={audioUrl || undefined}
                  controls
                  onPlay={() => setPlayingKokoro(true)}
                  onPause={() => setPlayingKokoro(false)}
                  onEnded={() => setPlayingKokoro(false)}
                  className={`w-full ${isHigh && audioUrl ? "" : "hidden"}`}
                  data-testid="kokoro-audio"
                />
                {!(isHigh && audioUrl) && (
                  <div className="flex items-center gap-1 w-full opacity-40" aria-hidden>
                    {Array.from({ length: 60 }).map((_, i) => (
                      <span key={i} className="h-1 w-1 bg-zinc-700" />
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* SIDEBAR */}
            <aside className="bg-[#0d0d0d] flex flex-col">
              {/* ENGINE */}
              <div className="px-6 py-5 border-b border-zinc-900">
                <span className="text-[11px] tracking-[0.25em] uppercase text-zinc-500 block mb-4">
                  Engine
                </span>
                <div className="grid grid-cols-2 border border-zinc-800" data-testid="engine-toggle">
                  <button
                    onClick={() => switchEngine("browser")}
                    data-testid="engine-browser-btn"
                    className={`flex flex-col gap-1 px-4 py-3 text-left transition-colors duration-100 ${
                      !isHigh ? "bg-zinc-100 text-black" : "bg-[#0a0a0a] text-zinc-500 hover:text-white"
                    }`}
                  >
                    <span className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide">
                      <Zap size={15} strokeWidth={2.5} /> Low Quality
                    </span>
                    <span className="text-[10px] tracking-[0.2em] uppercase opacity-70">Instant</span>
                  </button>
                  <button
                    onClick={() => switchEngine("kokoro")}
                    data-testid="engine-kokoro-btn"
                    className={`flex flex-col gap-1 px-4 py-3 text-left transition-colors duration-100 border-l border-zinc-800 ${
                      isHigh ? "bg-volt text-black" : "bg-[#0a0a0a] text-zinc-500 hover:text-white"
                    }`}
                  >
                    <span className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide">
                      <Cpu size={15} strokeWidth={2.5} /> High Quality
                    </span>
                    <span className="text-[10px] tracking-[0.2em] uppercase opacity-70">ONNX · WAV</span>
                  </button>
                </div>
                <p className="text-[11px] tracking-[0.15em] uppercase text-zinc-600 mt-3">
                  {isHigh ? "// Kokoro 82M neural · WAV export" : "// System voices · instant, no export"}
                </p>
              </div>

              {/* FREE QUOTA */}
              <div className="px-6 py-5 border-b border-zinc-900" data-testid="quota-panel">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] tracking-[0.25em] uppercase text-zinc-500">
                    Free Quota
                  </span>
                  <span className="text-[11px] tracking-widest uppercase text-zinc-500">
                    {isHigh ? "High Quality" : "Low Quality"}
                  </span>
                </div>

                {isPaid ? (
                  <div className="flex items-center gap-2 text-volt" data-testid="quota-unlimited">
                    <InfinityIcon size={18} />
                    <span className="text-sm">No character limit on your plan</span>
                  </div>
                ) : (
                  <>
                    <div className="h-2 w-full bg-zinc-800 mb-2">
                      <div
                        className={`h-full transition-[width] duration-300 ${
                          quotaLeft === 0 ? "bg-red-500" : "bg-volt"
                        }`}
                        style={{ width: `${quotaPct}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[11px] font-mono">
                      <span className="text-zinc-400" data-testid="quota-used">
                        {quotaUsed.toLocaleString()} / {quotaLimit.toLocaleString()} chars
                      </span>
                      <span
                        className={quotaLeft === 0 ? "text-red-500" : "text-volt"}
                        data-testid="quota-left"
                      >
                        {quotaLeft.toLocaleString()} left
                      </span>
                    </div>

                    {!user && (
                      <p className="text-[11px] text-zinc-600 mt-2 leading-relaxed">
                        Hard cap of {quotaLimit.toLocaleString()} free characters. Sign in to start tracking your usage.
                      </p>
                    )}

                    {user && (
                      <>
                        <div className="mt-2 flex items-center justify-between text-[11px] font-mono">
                          <span className={willExceed ? "text-red-500" : "text-zinc-500"}>
                            This text: {text.length.toLocaleString()} chars
                          </span>
                          {willExceed && (
                            <span className="text-red-500 uppercase tracking-wide">Exceeds limit</span>
                          )}
                        </div>
                        <button
                          onClick={() => openPricing()}
                          data-testid="quota-upgrade-btn"
                          className="mt-3 w-full text-[11px] font-bold uppercase tracking-widest text-black bg-volt py-2 hover:bg-volt-hover transition-colors duration-100"
                        >
                          Remove Limit — Go Unlimited
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>

              {/* VOICE */}
              <div className="px-6 py-5 border-b border-zinc-900">
                <span className="text-[11px] tracking-[0.25em] uppercase text-zinc-500 block mb-4">
                  Voice
                </span>
                {!isHigh ? (
                  <Select value={browserVoiceURI} onValueChange={setBrowserVoiceURI}>
                    <SelectTrigger
                      data-testid="browser-voice-select"
                      className="rounded-none bg-[#151515] border-zinc-800 h-12 text-white font-mono text-sm focus:ring-1 focus:ring-white"
                    >
                      <SelectValue placeholder={browser.voices.length ? "Select a voice" : "No system voices available"} />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[#151515] border-zinc-800 text-white max-h-72 font-mono">
                      {englishVoices.length > 0 && (
                        <SelectGroup>
                          <SelectLabel className="text-zinc-500 text-xs uppercase tracking-widest">English</SelectLabel>
                          {englishVoices.map((v) => (
                            <SelectItem key={v.voiceURI} value={v.voiceURI} className="rounded-none text-sm focus:bg-white focus:text-black">
                              {v.name} · {v.lang}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                      {otherVoices.length > 0 && (
                        <SelectGroup>
                          <SelectLabel className="text-zinc-500 text-xs uppercase tracking-widest">Other</SelectLabel>
                          {otherVoices.map((v) => (
                            <SelectItem key={v.voiceURI} value={v.voiceURI} className="rounded-none text-sm focus:bg-white focus:text-black">
                              {v.name} · {v.lang}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                ) : (
                  <Select value={kokoroVoice} onValueChange={setKokoroVoice}>
                    <SelectTrigger
                      data-testid="kokoro-voice-select"
                      className="rounded-none bg-[#151515] border-zinc-800 h-12 text-white font-mono text-sm uppercase focus:ring-1 focus:ring-volt"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[#151515] border-zinc-800 text-white max-h-72 font-mono">
                      {KOKORO_VOICES.map((v) => (
                        <SelectItem key={v.id} value={v.id} className="rounded-none text-sm uppercase focus:bg-volt focus:text-black">
                          {v.label} · EN-{v.accent} · {v.gender}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* MODEL / STATUS */}
              <div className="px-6 py-5 border-b border-zinc-900">
                {isHigh ? (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-500">Model · Kokoro-82M</span>
                      <span
                        data-testid="kokoro-status"
                        className={`flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase ${
                          kokoro.status === "ready" ? "text-volt" : kokoro.status === "error" ? "text-red-500" : "text-zinc-400"
                        }`}
                      >
                        {kokoro.status === "ready" && <CheckCircle2 size={13} />}
                        {modelStatusText()}
                      </span>
                    </div>
                    {kokoro.status === "loading" && (
                      <div className="h-1 w-full bg-zinc-800 mb-3">
                        <div className="h-full bg-volt transition-[width] duration-200" style={{ width: `${kokoro.progress}%` }} />
                      </div>
                    )}
                    <p className="text-xs text-zinc-500 leading-relaxed">
                      Model runs in-browser. Your text stays on this device while audio is generated.
                    </p>
                  </>
                ) : (
                  <div className="flex items-center gap-2" data-testid="browser-status">
                    <span className={`h-2 w-2 ${browser.supported ? "bg-white" : "bg-red-500"}`} />
                    <span className="text-[11px] tracking-[0.15em] uppercase text-zinc-400">
                      {browser.supported ? `${browser.voices.length} system voices` : "Not supported here"}
                    </span>
                  </div>
                )}
              </div>

              {/* SPEED / PITCH */}
              <div className="px-6 py-5 flex flex-col gap-6 mt-auto">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[11px] tracking-[0.25em] uppercase text-zinc-500">
                      {isHigh ? "Speed" : "Rate"}
                    </span>
                    <span data-testid="speed-slider-value" className="text-sm text-[#3b82f6]">
                      {(isHigh ? speed : rate).toFixed(2)}x
                    </span>
                  </div>
                  <FilledSlider
                    value={isHigh ? speed : rate}
                    min={0.5}
                    max={2}
                    step={0.05}
                    color="#3b82f6"
                    onChange={isHigh ? setSpeed : setRate}
                    testid="speed-slider"
                  />
                </div>
                {!isHigh && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[11px] tracking-[0.25em] uppercase text-zinc-500">Pitch</span>
                      <span data-testid="pitch-slider-value" className="text-sm text-[#3b82f6]">
                        {pitch.toFixed(2)}
                      </span>
                    </div>
                    <FilledSlider value={pitch} min={0} max={2} step={0.05} color="#3b82f6" onChange={setPitch} testid="pitch-slider" />
                  </div>
                )}
              </div>
            </aside>
          </div>

          {/* ACTION BAR */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-[1px] bg-zinc-800">
            <button
              onClick={handlePlay}
              disabled={kokoro.generating}
              data-testid="play-btn"
              className="flex items-center justify-center gap-2 bg-[#FF3B30] text-white hover:brightness-110 py-5 text-sm font-bold uppercase tracking-widest transition-[filter] duration-100 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white"
            >
              <Play size={16} strokeWidth={2.5} />
              {isHigh && kokoro.status !== "ready" ? "Load Model" : browser.paused ? "Resume" : "Play"}
            </button>
            <button
              onClick={handlePause}
              data-testid="pause-btn"
              className="flex items-center justify-center gap-2 bg-[#141414] text-zinc-300 hover:text-white hover:bg-[#1c1c1c] py-5 text-sm font-bold uppercase tracking-widest transition-colors duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white"
            >
              <Pause size={16} strokeWidth={2.5} /> Pause
            </button>
            <button
              onClick={handleStop}
              data-testid="stop-btn"
              className="flex items-center justify-center gap-2 bg-[#141414] text-zinc-300 hover:text-white hover:bg-[#1c1c1c] py-5 text-sm font-bold uppercase tracking-widest transition-colors duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white"
            >
              <Square size={16} strokeWidth={2.5} /> Stop
            </button>
            <button
              onClick={handleGenerate}
              disabled={kokoro.generating}
              data-testid="generate-btn"
              className="flex items-center justify-center gap-2 bg-[#141414] text-zinc-300 hover:text-white hover:bg-[#1c1c1c] py-5 text-sm font-bold uppercase tracking-widest transition-colors duration-100 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white"
            >
              <RotateCw size={16} strokeWidth={2.5} className={kokoro.generating ? "animate-spin" : ""} /> Generate
            </button>
            <button
              onClick={handleDownload}
              data-testid="download-btn"
              className="flex items-center justify-center gap-2 bg-[#2563EB] text-white hover:brightness-110 py-5 text-sm font-bold uppercase tracking-widest transition-[filter] duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white"
            >
              <Download size={16} strokeWidth={2.5} /> WAV
            </button>
          </div>
        </div>

        <p className="text-[11px] text-zinc-600 tracking-wider mt-4 max-w-[1400px] mx-auto w-full">
          Speech generation is client-side · your text is never uploaded
        </p>
      </div>
    </div>
  );
}
